import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function Hero() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Artistic%20editorial%20flat%20lay%20composition%20with%20trendy%20sneakers%20designer%20sunglasses%20gold%20watch%20leather%20wallet%20and%20minimalist%20tote%20bag%20scattered%20across%20textured%20oatmeal%20linen%20fabric%20bathed%20in%20warm%20golden%20hour%20sunlight%20casting%20long%20elegant%20shadows%20high%20fashion%20ecommerce%20aesthetic%20with%20organic%20dried%20flowers%20and%20neutral%20stone%20accents&width=1920&height=1080&seq=hero-bg-v2-001&orientation=landscape"
          alt="Kakobuy Spreadsheet produkty"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/30"></div>
      </div>

      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center pt-20 pb-12">
        <span className="inline-block px-5 py-2 border border-white/40 rounded-full text-white text-xs md:text-sm font-medium tracking-wide mb-6 md:mb-8 backdrop-blur-sm">
          {t('hero.badge')}
        </span>

        <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading font-semibold text-white leading-tight mb-4 md:mb-6 text-balance">
          {t('hero.title.line1')}
          <br />
          {t('hero.title.line2')}
        </h1>

        <div ref={dropdownRef} className="relative inline-block mb-4 md:mb-6">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center justify-between gap-3 px-6 py-3 bg-white/15 backdrop-blur-md border border-white/30 rounded-full text-white text-sm md:text-base min-w-[280px] md:min-w-[340px] cursor-pointer hover:bg-white/25 transition-all duration-200"
          >
            <span className="text-white/80">{t('hero.dropdown.placeholder')}</span>
            <div className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
              <i className="ri-arrow-down-s-line"></i>
            </div>
          </button>

          {isOpen && (
            <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-2xl shadow-lg border border-background-200 overflow-hidden z-50 max-h-64 overflow-y-auto">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setIsOpen(false)}
                  className="block px-5 py-3 text-foreground-700 text-sm hover:bg-primary-50 hover:text-primary-700 transition-colors text-left whitespace-nowrap cursor-pointer"
                >
                  {t(cat.nameKey)}
                </a>
              ))}
            </div>
          )}
        </div>

        <p className="text-white/75 text-sm md:text-base mb-8 md:mb-10 max-w-lg mx-auto leading-relaxed">
          {t('hero.subtitle.line1')}
          <br />
          {t('hero.subtitle.line2')}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-2 bg-foreground-900 hover:bg-foreground-950 text-white text-sm md:text-base font-medium px-8 py-3.5 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            {t('hero.cta.sheet')}
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </div>
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-white text-sm md:text-base font-medium px-8 py-3.5 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            {t('hero.cta.buy')}
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-shopping-bag-line"></i>
            </div>
          </a>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-6 mt-10 md:mt-12">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-2 text-white/80 text-xs md:text-sm">
              <div className="w-4 h-4 flex items-center justify-center text-primary-300">
                <i className="ri-check-line"></i>
              </div>
              <span>{t(`about.trust.item${i}`)}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}